import { useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { t, isRTL } from '../../utils/i18n';
import { trackDisplay } from '../../utils/tracks';

export type AppScreen = 'dashboard' | 'practice' | 'weak_practice' | 'exam' | 'exam_practice' | 'achievements' | 'leaderboard' | 'goals' | 'settings' | 'duel' | 'lessons' | 'lesson_detail' | 'learning_path' | 'admin' | 'tutor_dashboard' | 'tutor_overview' | 'practice_hub' | 'progress';

interface AppShellProps {
  screen: AppScreen;
  onNavigate: (screen: AppScreen) => void;
  children: React.ReactNode;
}


// Bottom nav mirrors the three home-screen lanes plus dashboard and settings.
// The lane screens (lessons / practice_hub / progress) are themselves hubs that
// fan out to the specific modes (exam, weak_practice, duel, goals, achievements,
// leaderboard, etc.), so they don't need their own bottom-nav slot.
const BOTTOM_NAV_IDS: AppScreen[] = ['dashboard', 'lessons', 'practice_hub', 'progress', 'settings'];
// Teachers get a slimmed nav — they don't practice/take exams; they follow
// tutees. Both the desktop pill row and the mobile bar use this set.
const TEACHER_BOTTOM_NAV_IDS: AppScreen[] = ['tutor_dashboard', 'settings'];

export default function AppShell({ screen, onNavigate, children }: AppShellProps) {
  const { student, lang } = useAuth();
  const rtl = isRTL(lang);
  const isTeacher = student?.role === 'teacher';

  // Nav items with translated labels (memoized to avoid rebuilding every render)
  const navItems = useMemo(() => {
    if (isTeacher) {
      return [
        { id: 'tutor_dashboard' as AppScreen, icon: '📚', label: 'התלמידים שלי', activeScreens: ['tutor_dashboard', 'tutor_overview'] as AppScreen[] },
        { id: 'settings' as AppScreen, icon: '⚙️', label: t('nav_settings', lang), activeScreens: ['settings'] as AppScreen[] },
      ];
    }
    // Top + bottom nav mirror the three lanes. Goals / achievements /
    // leaderboard / weak / exam / duel / lesson-detail all roll up to one of
    // the three lanes via `activeScreens` so the bottom-nav stays highlighted
    // when the user is deep inside a flow that originated from a lane.
    return [
      { id: 'dashboard' as AppScreen,    icon: '🏠', label: t('nav_dashboard', lang), activeScreens: ['dashboard'] as AppScreen[] },
      { id: 'lessons' as AppScreen,      icon: '📚', label: 'ללמוד',                   activeScreens: ['lessons', 'lesson_detail', 'learning_path'] as AppScreen[] },
      { id: 'practice_hub' as AppScreen, icon: '✏️', label: 'לתרגל',                   activeScreens: ['practice_hub', 'practice', 'weak_practice', 'exam', 'exam_practice', 'duel'] as AppScreen[] },
      { id: 'progress' as AppScreen,     icon: '📊', label: 'התקדמות',                 activeScreens: ['progress', 'goals', 'achievements', 'leaderboard'] as AppScreen[] },
      { id: 'settings' as AppScreen,     icon: '⚙️', label: t('nav_settings', lang),  activeScreens: ['settings'] as AppScreen[] },
    ];
  }, [lang, isTeacher]);

  const bottomNavIds = isTeacher ? TEACHER_BOTTOM_NAV_IDS : BOTTOM_NAV_IDS;

  return (
    <div className="min-h-screen flex flex-col" dir={rtl ? 'rtl' : 'ltr'}>
      {/* Skip to main content — keyboard accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:right-2 focus:z-50 focus:px-4 focus:py-2 focus:bg-accent focus:text-white focus:rounded-lg focus:text-sm focus:font-bold"
      >
        {t('skip_to_main', lang)}
      </a>

      {/* Top Nav */}
      <header className="sticky top-0 z-40 glass-card rounded-none border-x-0 border-t-0" role="banner">
        <div className="max-w-4xl mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-lg font-extrabold gradient-text" aria-label={`${t('app_title', lang)} — ${t('login_subtitle', lang)}`}>{t('app_title', lang)}</span>
            <span className="text-xs text-slate-400">{trackDisplay(student?.track, lang)}</span>
          </div>
          <nav className="hidden sm:flex items-center gap-1 overflow-x-auto" aria-label={t('nav_main', lang)}>
            {/* Settings is rendered as a dedicated gear-icon button on the right;
                exclude it from the pill row to avoid a duplicate desktop entry. */}
            {navItems.filter(item => item.id !== 'settings').map((item) => {
              const isActive = item.activeScreens.includes(screen);
              return (
                <button
                  key={item.id}
                  onClick={() => onNavigate(item.id)}
                  aria-current={isActive ? 'page' : undefined}
                  data-tour={`nav-${item.id}`}
                  className={`text-xs px-3 py-2 min-h-[44px] rounded-full transition whitespace-nowrap focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-1 focus-visible:ring-offset-navy-900 outline-none ${
                    isActive ? 'bg-accent/20 text-accent-light font-bold' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  <span aria-hidden="true">{item.icon}</span> {item.label}
                </button>
              );
            })}
          </nav>
          <button
            onClick={() => onNavigate('settings')}
            aria-label={t('settings_title', lang)}
            className={`text-xs px-2 py-2 min-h-[44px] min-w-[44px] rounded-full transition hidden sm:flex items-center justify-center focus-visible:ring-2 focus-visible:ring-accent outline-none ${
              screen === 'settings' ? 'text-accent-light' : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            <span aria-hidden="true">⚙️</span>
          </button>
        </div>
      </header>

      {/* Content */}
      <main id="main-content" className="flex-1 flex items-start justify-center px-4 pb-24 sm:pb-8 pt-2" role="main" style={{ paddingBottom: 'max(6rem, calc(env(safe-area-inset-bottom) + 5rem))' }}>
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-40 bottom-nav" aria-label={t('nav_bottom', lang)} style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <div className="flex justify-around py-2">
          {navItems.filter(item => bottomNavIds.includes(item.id)).map((item) => {
            const isActive = item.activeScreens.includes(screen);
            return (
              <button
                key={item.id}
                onClick={() => onNavigate(item.id)}
                aria-current={isActive ? 'page' : undefined}
                aria-label={item.label}
                data-tour={`nav-${item.id}-mobile`}
                className={`flex flex-col items-center gap-0.5 px-3 py-2 min-h-[48px] min-w-[48px] transition focus-visible:ring-2 focus-visible:ring-accent outline-none ${
                  isActive ? 'text-accent-light' : 'text-slate-500'
                }`}
              >
                <span className="text-lg" aria-hidden="true">{item.icon}</span>
                <span className="text-[10px]">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
